import React, { useRef, useEffect } from 'react';
import { Paper, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { NumericMetricInfo } from '@moderepo/mode-apis';
import { ChartValueType } from '../../containers/robot-cloud/RCUtils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            height                      : '100%',
            padding                     : theme.spacing(1),
            display                     : 'flex',
            flexDirection               : 'column',
            alignItems                  : 'center',
            justifyContent              : 'center',
            [theme.breakpoints.up('md')]: {
                padding: theme.spacing(1.5),
            },
        },

        metricValue: {
            display       : 'flex',
            flexDirection : 'column',
            flex          : 1,
            alignItems    : 'center',
            justifyContent: 'flex-start',
            width         : '100%',
            height        : '100%',
        },

        chart: {
            width    : '100%',
            minHeight: 200,
            height   : '100%',
        },

        metricValueName: {
            color                       : theme.palette.text.secondary,
            fontWeight                  : 500,
            fontSize                    : 16,
            textAlign                   : 'center',
            [theme.breakpoints.up('md')]: {
                fontSize: 18,
            },
        },

        metricValueUnit: {
            paddingLeft: 5,
        },
    };
});



export interface GaugeChartStyleOptions {
    readonly gaugeColor?: string;
    readonly handColor?: string;
}


/**
 * Create a generic Axis which can be used to display values that are just value, no special meaning. These are the kind of values that doesn't
 * have special meaning e.g. low value means bad and high values mean good.
 */
const createDefaultAxis = (
    chart: am4charts.GaugeChart,
    valueRange: {readonly min?: number | undefined, readonly max?: number | undefined} | undefined,
    options?: GaugeChartStyleOptions,
): any => {

    // Create the axis
    const axis = chart.xAxes.push(new am4charts.ValueAxis() as any);
    axis.strictMinMax = true;
    axis.renderer.inside = true;
    axis.renderer.minGridDistance = 100;
    axis.renderer.line.strokeOpacity = 1;
    axis.renderer.line.strokeWidth = 5;
    axis.renderer.line.stroke = am4core.color(options?.gaugeColor || '#8067dc');
    if (valueRange) {
        axis.min = valueRange.min;
        axis.max = valueRange.max;
    } else {
        axis.min = 0;
        axis.max = 100;
    }


    // Customize the axis
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.stroke = am4core.color('black');
    axis.renderer.ticks.template.strokeOpacity = 0.5;
    axis.renderer.ticks.template.length = 7;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = 30;
    axis.renderer.labels.template.fontSize = '0.9em';

    return axis;
};



/**
 * Create Gauge axis for a gauge that show Good/Bad values. This kind of gauge will have a green -> red arc which can be used for value which has
 * value that goes from good to bad e.g. 0 = good and 100 = bad. This gauge will have red -> green arc for the kind of value that has the opposite
 * meaning.
 *
 * @param chart
 * @param isReverse
 */
const createPositiveNegativeAxis = (
    chart: am4charts.GaugeChart,
    valueRange: {readonly min?: number | undefined, readonly max?: number | undefined} | undefined,
    isReverse: boolean = false,
): any => {

    // Create a gradient color
    const gradient = new am4core.LinearGradient();

    // Start value will be green unless isReverse is true
    if (isReverse) {
        gradient.stops.push({ color: am4core.color('red') });
    } else {
        gradient.stops.push({ color: am4core.color('green') });
    }

    // middle color
    gradient.stops.push({ color: am4core.color('yellow') });

    // End value will be red unless isReverse is true
    if (isReverse) {
        gradient.stops.push({ color: am4core.color('green') });
    } else {
        gradient.stops.push({ color: am4core.color('red') });
    }

    // Create the axis
    const axis = chart.xAxes.push(new am4charts.ValueAxis() as any);
    axis.strictMinMax = true;
    axis.renderer.inside = true;
    axis.renderer.minGridDistance = 100;
    axis.renderer.line.strokeOpacity = 1;
    axis.renderer.line.strokeWidth = 15;
    axis.renderer.line.stroke = gradient;
    if (valueRange) {
        axis.min = valueRange.min;
        axis.max = valueRange.max;
    } else {
        axis.min = 0;
        axis.max = 100;
    }


    // Customize the axis
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.stroke = am4core.color('black');
    axis.renderer.ticks.template.strokeOpacity = 0.5;
    axis.renderer.ticks.template.length = 17;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = 40;
    axis.renderer.labels.template.fontSize = '0.9em';

    return axis;
};


interface GaugeMetricValueDisplayProps {
    readonly className?: string;
    readonly numericMetric: NumericMetricInfo;
    readonly value: number;
    readonly displayValue: string;
    readonly valueName: string;
    readonly type: ChartValueType;
    readonly style?: GaugeChartStyleOptions;
}


/**
 * This is a Component that use a Gauge to display metric value. There are a few different UI for a Gauge and the user can customize it by
 * specifying the ChartValueType or props.style options.
 *
 * @param props
 */
export const GaugeMetricValueDisplay = (props: GaugeMetricValueDisplayProps) => {
    const chartRef = useRef<am4charts.GaugeChart>();
    const valueLabelRef = useRef<am4core.Label>();
    const unitLabelRef = useRef<am4core.Label>();
    const displayFieldRef = useRef<string>();
    const metricRef = useRef<NumericMetricInfo>();
    const typeRef = useRef<ChartValueType>();
    const optionsRef = useRef<GaugeChartStyleOptions>();
    const classes = useStyle();



    // This useEffect is used for cleaning up Chart when component unmounted
    useEffect(() => {
        // Return cleanup function
        return () => {
            const chart = chartRef.current as am4charts.GaugeChart;
            if (chart && !chart.isDisposed()) {
                // console.log('dispose chart');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }
        };

    }, []);



    /**
     * This useEffect is used for creating/updating the chart. This useEffect depends on a lot of props and when each props change, this useEffect
     * will be called. So, it will be very slow if we were to re-create the chart every time this useEffect is called. We can't remove any of the
     * dependencies because the chart needs all the dependencies. So to solve this problem, we will keep a reference of the PREVIOUS prop values.
     * Each time this useEffect is called, we will compare the new prop values with the previous to determine which props changed. Some props
     * will always require a new chart to be created e.g. displayFields when they are changed. Some props does not which mean we can
     * just update the chart's properties when component props changed.
     */
    useEffect(() => {

        let chart = chartRef.current as am4charts.GaugeChart;

        // We will need to re-create chart if chartId changed or displayMetrics changed
        const createNewChart: boolean = displayFieldRef.current !== props.valueName;

        if (!chart || createNewChart) {

            if (chart && !chart.isDisposed()) {
                // Dispose old chart first
                // console.log('dispose chart');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }

            // create new Chart
            chart = am4core.create(props.valueName, am4charts.GaugeChart);
            chart.hiddenState.properties.opacity = 0; // this makes initial fade in effect
            chart.innerRadius = -15;
            chart.startAngle = 160;
            chart.endAngle = 380;


            // Create a label for displaying the unit
            const unitLabel = chart.radarContainer.createChild(am4core.Label);
            unitLabel.isMeasured = false;
            unitLabel.fill = am4core.color('#999999');
            unitLabel.fontSize = 20;
            unitLabel.fontWeight = 'bold';
            unitLabel.x = am4core.percent(50);
            unitLabel.horizontalCenter = 'middle';
            unitLabel.verticalCenter = 'middle';        // center the value vertically and horizontally
            unitLabel.y = -30;                           // Move the value down some pixel
            unitLabelRef.current = unitLabel;


            // Create a label for displaying the value
            const label = chart.radarContainer.createChild(am4core.Label);
            label.isMeasured = false;
            label.fontSize = 30;
            label.fontWeight = 'bold';
            label.x = am4core.percent(50);
            label.horizontalCenter = 'middle';
            label.verticalCenter = 'middle';        // center the value vertically and horizontally
            label.y = 30;                           // Move the value down some pixel
            label.text = '0';
            valueLabelRef.current = label;
        }


        if (createNewChart || metricRef.current !== props.numericMetric || typeRef.current !== props.type || optionsRef.current !== props.style) {
            while (chart.xAxes.length > 0) {
                chart.xAxes.removeIndex(0).dispose();
            }

            if (unitLabelRef.current && props.numericMetric && props.numericMetric.unit) {
                unitLabelRef.current.text = props.numericMetric.unit;
            }

            // Create Axis, the Gauge UI
            switch (props.type) {
                case ChartValueType.POSITIVE_TO_NEGATIVE:
                case ChartValueType.NEGATIVE_TO_POSITIVE:
                    createPositiveNegativeAxis(chart, props.numericMetric.range, props.type === ChartValueType.NEGATIVE_TO_POSITIVE);
                    break;
                default:
                    createDefaultAxis(chart, props.numericMetric.range, props.style);
            }
        }


        if (createNewChart || optionsRef.current !== props.style) {
            // IMPORTANT NOTE: need to create hand AFTER axis
            const hand = chart.hands.push(new am4charts.ClockHand());
            // hand.pin.disabled = true;
            hand.startWidth = 5;
            hand.endWidth = 0;
            hand.radius = am4core.percent(90);
            // hand.innerRadius = am4core.percent(45);
            hand.fill = am4core.color(props.style?.handColor || '#000000');
            hand.stroke = am4core.color(props.style?.handColor || '#000000');
            hand.showValue(0);
        }


        // Update the refs so that we can compare them again later
        displayFieldRef.current = props.valueName;
        metricRef.current = props.numericMetric;
        typeRef.current = props.type;
        optionsRef.current = props.style;
        chartRef.current = chart;
    }, [props.valueName, props.numericMetric, props.type, props.style]);


    /**
     * This useEffect is for monitoring the props.data value change to update the chart hand and value display.
     */
    useEffect(() => {
        const chart = chartRef.current as am4charts.GaugeChart;
        if (chart && chart.hands.length > 0) {
            const hand: am4charts.ClockHand = chart.hands.getIndex(0) as am4charts.ClockHand;
            const axis = chart.xAxes.getIndex(0) as any;

            // Update hand but make sure the hand doesn't go below MIN value or over MAX value.
            hand.showValue(Math.max(axis.min as number, Math.min(axis.max as number, props.value)), 500, am4core.ease.cubicOut);

            // Update label to show the value. For label, we can show the actual value, doesn't matter if it is over or under min/max
            if (valueLabelRef.current) {
                valueLabelRef.current.text = props.displayValue;
            }
        }
    }, [props.value, props.valueName, props.displayValue]);



    return (
        <Paper className={clsx(classes.root, props.className)}>
            <div className={classes.metricValue}>
                <div className={classes.chart} id={props.valueName} />
            </div>
            <div className={classes.metricValueName}>
                {props.numericMetric ? props.numericMetric.displayName || props.numericMetric.name : props.valueName}
                {props.numericMetric && props.numericMetric.unit && (
                    <span className={classes.metricValueUnit}>[{props.numericMetric.unit}]</span>
                )}
            </div>
        </Paper>
    );
};
